import { type BookingTimeline, type LeadPriority } from '../schemas/leads';

/**
 * Auto-computed "Lead Intent" scoring (rubric confirmed 2026-08-06,
 * duplicated to shared 2026-08-10 so the admin UI can render a "Suggested"
 * hint alongside the Lead Intent picker).
 *
 * Weighted points:
 *   +2 valid passport (ready to fly international)   / -2 explicitly NO passport
 *   +2 travelling within ~3 months                   / -2 more than 3 months out
 *   +2 premium / luxury travel-mode preference
 *   +2 budget >= threshold
 *   +1 flights NOT booked (needs the full package = more revenue for us)
 * Tiers: HIGH >= 6, MEDIUM (NORMAL) 3-5, otherwise BLANK (null).
 *
 * A weak lead is left blank rather than actively down-ranked, and URGENT is
 * never auto-assigned - both stay operator-only decisions.
 *
 * Kept in sync with `apps/api/src/modules/leads/lead-intent.helper.ts`;
 * that file re-exports from here (source of truth).
 */
export interface LeadIntentSignals {
  hasValidPassport?: boolean | null;
  isFlightBooked?: boolean | null;
  bookingTimeline?: BookingTimeline | null;
  preferredTravelMode?: string | null;
  budgetAmount?: number | null;
}

/** Budget (rupees) at/above which the lead earns the budget signal. */
export const LEAD_INTENT_BUDGET_THRESHOLD = 150_000;

const NEAR_TERM_TIMELINES: ReadonlySet<BookingTimeline> = new Set<BookingTimeline>([
  'WITHIN_7_DAYS',
  'EIGHT_TO_15_DAYS',
  'SIXTEEN_TO_30_DAYS',
  'ONE_TO_THREE_MONTHS',
]);

/** Tiers the auto-scorer can assign: HIGH, NORMAL (= "Medium"), or null. */
export type AutoLeadIntent = Extract<LeadPriority, 'HIGH' | 'NORMAL'> | null;

/** Score the captured signals into a Lead Intent tier (null = leave blank). */
export function scoreLeadIntent(signals: LeadIntentSignals): AutoLeadIntent {
  let points = 0;

  if (signals.hasValidPassport === true) points += 2;
  else if (signals.hasValidPassport === false) points -= 2;

  if (signals.bookingTimeline && NEAR_TERM_TIMELINES.has(signals.bookingTimeline)) points += 2;
  else if (signals.bookingTimeline === 'MORE_THAN_THREE_MONTHS') points -= 2;

  const mode = (signals.preferredTravelMode ?? '').toLowerCase();
  if (/premium|luxury/.test(mode)) points += 2;

  if (
    typeof signals.budgetAmount === 'number' &&
    signals.budgetAmount >= LEAD_INTENT_BUDGET_THRESHOLD
  ) {
    points += 2;
  }

  if (signals.isFlightBooked === false) points += 1;

  if (points >= 6) return 'HIGH';
  if (points >= 3) return 'NORMAL';
  return null;
}
